import * as React from 'react';
import { useState, useEffect } from 'react';
import Dropdown, { Option } from '@app/react-common/components/dropdown/Dropdown';
import Button from '@app/react-common/components/button/Button';
import DatePicker from '@app/react-common/components/datepicker/DatePicker';
import { countryList, deliveryPromiseSettings, weekendDays } from '@pages/checkout-app/configs';
import { WEEKDAY_OPTIONS } from '@pages/checkout-app/components/fee-per-weekday/fee-per-weekday.component';
import { Checkout } from '@pdqdev/pdq-shared/dist/interfaces';
import { parseLocalDate } from 'src/utils';
interface TypeOption {
  type: string;
  value: string;
}

interface NotCountedAsBusinessDaysProps {
  onChange?: (items: Checkout.NonBusinessDaysItem[]) => void;
  existingItems?: Checkout.NonBusinessDaysItem[];
}

interface AddedItem {
  id: string;
  type: string;
  value: any;
  displayText: string;
}

const getTodayLocalDate = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = today.getMonth() + 1;
  const day = today.getDate();
  return new Date(year, month - 1, day);
};

// without X Days After, Custom Date, X Days From Today
const EXCLUDE_WEEKDAY_OPTIONS = [9, 10, 11];

const WEEKDAY_OPTIONS_FILTERED = WEEKDAY_OPTIONS.filter((o) => !EXCLUDE_WEEKDAY_OPTIONS.includes(o.value)).map((o) => ({
  name: o.label,
  value: o.value,
}));

// without Blackout date range
const TYPE_DROPDOWN_OPTIONS: Option[] = deliveryPromiseSettings
  .filter((setting) => setting.value !== Checkout.NonBusinessDaysTypeValue.CUSTOM_DAYS)
  .map((setting) => ({
    label: setting.type,
    value: setting.value,
  }));

export const NotCountedAsBusinessDays: React.FC<NotCountedAsBusinessDaysProps> = ({ onChange, existingItems = [] }) => {
  const [typeSelect, setTypeSelect] = useState<TypeOption | null>(null);
  const [selectedValue, setSelectedValue] = useState<string | Checkout.CustomDateValue | null>(null);
  const [noPickYet, setNoPickYet] = useState<boolean>(false);
  const [addedItems, setAddedItems] = useState<AddedItem[]>([]);

  useEffect(() => {
    if (existingItems && existingItems.length > 0) {
      const convertedItems = convertGroupedItemsToAddedItems(existingItems);
      setAddedItems(convertedItems);
    }
  }, [existingItems]);

  const getSecondDropdownOptions = (typeValue: string): Option[] => {
    switch (typeValue) {
      case Checkout.NonBusinessDaysTypeValue.WEEKEND_IN:
        return weekendDays.map((day) => ({
          label: day.name,
          value: day.value,
        }));
      case Checkout.NonBusinessDaysTypeValue.WEEKDAY_IS:
        return WEEKDAY_OPTIONS_FILTERED.map((day) => ({
          label: day.name,
          value: day.value,
        }));
      case Checkout.NonBusinessDaysTypeValue.NATIONAL_HOLIDAYS:
        return countryList().map((country) => ({
          label: country.name,
          value: country.value,
        }));
      default:
        return [];
    }
  };

  const getDynamicPlaceholder = (typeValue: string): string => {
    switch (typeValue) {
      case Checkout.NonBusinessDaysTypeValue.WEEKEND_IN:
        return 'Choose your weekend';
      case Checkout.NonBusinessDaysTypeValue.WEEKDAY_IS:
        return 'Choose your weekday';
      case Checkout.NonBusinessDaysTypeValue.NATIONAL_HOLIDAYS:
        return 'Select Holidays';
      default:
        return 'Select type first';
    }
  };

  const convertGroupedItemsToAddedItems = (groupedItems: Checkout.NonBusinessDaysItem[]): AddedItem[] => {
    const addedItems: AddedItem[] = [];

    groupedItems.forEach((groupedItem) => {
      const { type, value } = groupedItem;

      if (type === Checkout.NonBusinessDaysTypeValue.CUSTOM_DATE && Array.isArray(value)) {
        value.forEach((dateRange) => {
          addedItems.push({
            id: `${type}-${dateRange.startDate}-${dateRange.endDate}-${Date.now()}`,
            type: Checkout.NonBusinessDaysTypeName.CUSTOM_DATE,
            value: dateRange,
            displayText: generateDisplayText(type, dateRange),
          });
        });
      } else if (Array.isArray(value)) {
        value.forEach((itemValue) => {
          const displayType =
            type === Checkout.NonBusinessDaysTypeValue.WEEKEND_IN
              ? Checkout.NonBusinessDaysTypeName.WEEKEND_IN
              : type === Checkout.NonBusinessDaysTypeValue.WEEKDAY_IS
                ? Checkout.NonBusinessDaysTypeName.WEEKDAY_IS
                : type === Checkout.NonBusinessDaysTypeValue.NATIONAL_HOLIDAYS
                  ? Checkout.NonBusinessDaysTypeName.NATIONAL_HOLIDAYS
                  : type;

          addedItems.push({
            id: `${type}-${itemValue}-${Date.now()}`,
            type: displayType,
            value: itemValue,
            displayText: generateDisplayText(type, itemValue),
          });
        });
      }
    });

    return addedItems;
  };

  const generateDisplayText = (type: string, value: any): string => {
    if (type === Checkout.NonBusinessDaysTypeValue.CUSTOM_DATE) {
      const formatDate = (dateString: string) => {
        const date = parseLocalDate(dateString);
        return date ? date.toLocaleDateString() : '';
      };
      return `${formatDate(value.startDate)} - ${formatDate(value.endDate)}`;
    } else if (type === Checkout.NonBusinessDaysTypeValue.WEEKEND_IN) {
      const selectedWeekend = weekendDays.find((day) => day.value === value);
      return selectedWeekend ? selectedWeekend.name : value;
    } else if (type === Checkout.NonBusinessDaysTypeValue.WEEKDAY_IS) {
      const selectedWeekday = WEEKDAY_OPTIONS_FILTERED.find((day) => day.value === value);
      return selectedWeekday ? selectedWeekday.name : value;
    } else if (type === Checkout.NonBusinessDaysTypeValue.NATIONAL_HOLIDAYS) {
      const selectedCountry = countryList().find((country) => country.value === value);
      return selectedCountry ? selectedCountry.name : value;
    }
    return value;
  };

  const isDuplicateItem = (type: string, value: any): boolean => {
    return addedItems.some((item) => {
      if (type === Checkout.NonBusinessDaysTypeValue.CUSTOM_DATE) {
        return (
          item.type === Checkout.NonBusinessDaysTypeName.CUSTOM_DATE &&
          item.value.startDate === value.startDate &&
          item.value.endDate === value.endDate
        );
      } else if (type === Checkout.NonBusinessDaysTypeValue.WEEKEND_IN) {
        return item.type === Checkout.NonBusinessDaysTypeName.WEEKEND_IN && item.value === value;
      } else if (type === Checkout.NonBusinessDaysTypeValue.WEEKDAY_IS) {
        return item.type === Checkout.NonBusinessDaysTypeName.WEEKDAY_IS && item.value === value;
      } else if (type === Checkout.NonBusinessDaysTypeValue.NATIONAL_HOLIDAYS) {
        return item.type === Checkout.NonBusinessDaysTypeName.NATIONAL_HOLIDAYS && item.value === value;
      }
      return false;
    });
  };

  const groupItemsByType = (items: AddedItem[]): Checkout.NonBusinessDaysItem[] => {
    const grouped: { [key: string]: any[] } = {};

    items.forEach((item) => {
      const setting = deliveryPromiseSettings.find((s) => s.type === item.type);
      const type = setting?.value || item.type;

      if (!grouped[type]) {
        grouped[type] = [];
      }

      if (type === Checkout.NonBusinessDaysTypeValue.CUSTOM_DATE) {
        grouped[type].push({
          startDate: item.value.startDate,
          endDate: item.value.endDate,
        });
      } else {
        grouped[type].push(item.value);
      }
    });

    return Object.entries(grouped).map(([type, values]) => ({
      type,
      value: values as any,
    }));
  };

  const handleTypeDropdownChange = (option: Option) => {
    const selectedSetting = deliveryPromiseSettings.find((setting) => setting.value === option.value);
    setTypeSelect(selectedSetting || null);
    setSelectedValue(null);
    setNoPickYet(true);

    if (option.value === Checkout.NonBusinessDaysTypeValue.CUSTOM_DATE) {
      setSelectedValue({
        startDate: null,
        endDate: null,
      });
    }
  };

  const handleValueDropdownChange = (option: Option) => {
    setSelectedValue(option.value);
  };

  const handleCustomDateRangeChange = (dateRange: { start: string | null; end: string | null }) => {
    setSelectedValue({
      startDate: dateRange.start,
      endDate: dateRange.end,
    });
  };

  const validateAdd = (): boolean => {
    if (!typeSelect || selectedValue === null || selectedValue === undefined || !onChange) {
      return false;
    }

    if (typeSelect.value === Checkout.NonBusinessDaysTypeValue.CUSTOM_DATE) {
      if (typeof selectedValue === 'object' && selectedValue !== null) {
        if (!selectedValue.startDate || !selectedValue.endDate) {
          return false;
        }
      } else {
        return false;
      }
    }

    // Check if item already exists
    const isDuplicate = isDuplicateItem(typeSelect.value, selectedValue);
    if (isDuplicate) {
      return false;
    }

    return true;
  };

  const createNewItem = () => {
    return {
      id: `${typeSelect.value}-${Date.now()}`,
      type: typeSelect.type,
      value: selectedValue,
      displayText: generateDisplayText(typeSelect.value, selectedValue),
    };
  };

  const addItem = () => {
    const newItem: AddedItem = createNewItem();
    const updatedItems = [...addedItems, newItem];
    setAddedItems(updatedItems);
    const groupedItems = groupItemsByType(updatedItems);
    onChange(groupedItems);
  };

  const resetForm = () => {
    setTypeSelect(null);
    setSelectedValue(null);
    setNoPickYet(false);
  };

  const handleAdd = () => {
    if (!validateAdd()) {
      return;
    }

    addItem();
    resetForm();
  };

  const handleRemoveItem = (id: string) => {
    const updatedItems = addedItems.filter((item) => item.id !== id);
    setAddedItems(updatedItems);

    const groupedItems = groupItemsByType(updatedItems);
    if (onChange) {
      onChange(groupedItems);
    }
  };

  const isAddDisabled = () => {
    if (!typeSelect || selectedValue === null || selectedValue === undefined) {
      return true;
    }

    if (typeSelect.value === Checkout.NonBusinessDaysTypeValue.CUSTOM_DATE) {
      if (typeof selectedValue === 'object' && selectedValue !== null) {
        return !selectedValue.startDate || !selectedValue.endDate;
      }
      return true;
    }

    return false;
  };

  return (
    <div className="not-counted-as-business-days">
      <div className="not-counted-as-business-days__title">
        <h3>Exclude non-business days</h3>
      </div>
      <div className="not-counted-as-business-days__content">
        <div className="not-counted-as-business-days__dropdowns">
          <Dropdown
            options={TYPE_DROPDOWN_OPTIONS}
            placeholder="Pick one"
            preSelectedOption={
              typeSelect
                ? {
                    label: typeSelect.type,
                    value: typeSelect.value,
                  }
                : null
            }
            onSelect={handleTypeDropdownChange}
            width={130}
          />
          {typeSelect?.value === Checkout.NonBusinessDaysTypeValue.CUSTOM_DATE ? (
            <DatePicker
              variant="form"
              startDate={typeof selectedValue === 'object' && selectedValue !== null ? selectedValue.startDate : null}
              endDate={typeof selectedValue === 'object' && selectedValue !== null ? selectedValue.endDate : null}
              showPlaceholder={true}
              onChange={handleCustomDateRangeChange}
              width={230}
              minDate={getTodayLocalDate()}
            />
          ) : (
            <Dropdown
              key={typeSelect?.value || 'reset'}
              options={getSecondDropdownOptions(typeSelect?.value || '')}
              placeholder={getDynamicPlaceholder(typeSelect?.value || '')}
              preSelectedOption={
                selectedValue && typeof selectedValue === 'string'
                  ? getSecondDropdownOptions(typeSelect?.value || '').find((opt) => opt.value === selectedValue)
                  : null
              }
              onSelect={handleValueDropdownChange}
              width={200}
              disabled={!noPickYet}
            />
          )}
        </div>
        <Button
          onClick={handleAdd}
          text="Add"
          size="medium"
          variant="primary"
          disabled={isAddDisabled()}
          className="not-counted-as-business-days__add-button"
        />
      </div>

      {addedItems.length > 0 && (
        <div className="not-counted-as-business-days__added-items">
          <div className="not-counted-as-business-days__splitter"></div>
          {addedItems.map((item) => (
            <div key={item.id} className="not-counted-as-business-days__added-item-row">
              <div className="not-counted-as-business-days__content__custom-blackout-dates__box not-counted-as-business-days__type-box">
                {item.type}
              </div>
              <div className="not-counted-as-business-days__content__custom-blackout-dates__box not-counted-as-business-days__value-box">
                {item.displayText}
              </div>
              <button
                onClick={() => handleRemoveItem(item.id)}
                className="not-counted-as-business-days__remove-button"
                type="button">
                ×
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
